// Sign-in (docs/design/app.md §3): Cognito managed login for the app's client on the connector's user pool, the
// authorization code flow with PKCE S256 (a public client: no secret in the browser). Tokens live in sessionStorage,
// so closing the tab signs out; the refresh token (rotated, 12 hours) renews the hour-long access token.

export interface AppConfig {
  environment: string;
  /** https://<prefix>.auth.<region>.amazoncognito.com */
  loginDomain: string;
  clientId: string;
  /** The app's own URL, registered as the client's callback and sign-out URL. */
  redirectUri: string;
}

interface Tokens { access: string; refresh?: string; expiresAt: number }

const TOKENS = "tare.tokens";
const PENDING = "tare.signin";

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomString(bytes = 32): string {
  return base64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

/** The PKCE S256 challenge for a verifier. */
export async function challenge(verifier: string): Promise<string> {
  return base64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
}

export function authorizeUrl(cfg: AppConfig, state: string, codeChallenge: string): string {
  const q = new URLSearchParams({
    response_type: "code", client_id: cfg.clientId, redirect_uri: cfg.redirectUri, scope: "openid email",
    state, code_challenge: codeChallenge, code_challenge_method: "S256",
  });
  return `${cfg.loginDomain}/oauth2/authorize?${q}`;
}

export function logoutUrl(cfg: AppConfig): string {
  return `${cfg.loginDomain}/logout?${new URLSearchParams({ client_id: cfg.clientId, logout_uri: cfg.redirectUri })}`;
}

function read<T>(store: Storage, key: string): T | null {
  try {
    const v = store.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}

function save(t: { access_token: string; refresh_token?: string; expires_in: number }, previousRefresh?: string): void {
  const tokens: Tokens = {
    access: t.access_token, refresh: t.refresh_token ?? previousRefresh, expiresAt: Date.now() + t.expires_in * 1000,
  };
  sessionStorage.setItem(TOKENS, JSON.stringify(tokens));
}

async function tokenRequest(cfg: AppConfig, body: Record<string, string>) {
  const r = await fetch(`${cfg.loginDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cfg.clientId, ...body }),
  });
  if (!r.ok) throw new Error(`sign-in failed (${r.status})`);
  return (await r.json()) as { access_token: string; refresh_token?: string; expires_in: number };
}

/** Sends the person to Cognito's sign-in page. */
export async function signIn(cfg: AppConfig): Promise<never> {
  const state = randomString(16);
  const verifier = randomString(48);
  sessionStorage.setItem(PENDING, JSON.stringify({ state, verifier, back: location.pathname + location.search }));
  location.assign(authorizeUrl(cfg, state, await challenge(verifier)));
  return new Promise<never>(() => {});
}

/**
 * On the way back from Cognito (?code=&state=): exchanges the code, puts the address back as it was before sign-in.
 * Returns false if this page load isn't a sign-in callback.
 */
export async function completeSignIn(cfg: AppConfig): Promise<boolean> {
  const q = new URLSearchParams(location.search);
  if (!q.has("code") && !q.has("error")) return false;
  const pending = read<{ state: string; verifier: string; back: string }>(sessionStorage, PENDING);
  sessionStorage.removeItem(PENDING);
  // Cognito returns to the app's root; go back to where the person was (a path from this site only).
  history.replaceState(null, "", pending?.back?.startsWith("/") && !pending.back.startsWith("//") ? pending.back : "/");
  if (q.has("error")) throw new Error(`sign-in was refused: ${q.get("error_description") ?? q.get("error")}`);
  if (!pending || q.get("state") !== pending.state) throw new Error("sign-in didn't match this tab; sign in again");
  save(await tokenRequest(cfg, {
    grant_type: "authorization_code", code: q.get("code")!, redirect_uri: cfg.redirectUri, code_verifier: pending.verifier,
  }));
  return true;
}

/** A current access token, renewed if it is within two minutes of expiry; null when the person must sign in. */
export async function accessToken(cfg: AppConfig): Promise<string | null> {
  const t = read<Tokens>(sessionStorage, TOKENS);
  if (!t) return null;
  if (t.expiresAt - Date.now() > 120_000) return t.access;
  if (!t.refresh) return null;
  try {
    save(await tokenRequest(cfg, { grant_type: "refresh_token", refresh_token: t.refresh }), t.refresh);
    return read<Tokens>(sessionStorage, TOKENS)?.access ?? null;
  } catch {
    sessionStorage.removeItem(TOKENS);
    return null;
  }
}

export function forget(): void {
  sessionStorage.removeItem(TOKENS);
}

/** Signs out here and at Cognito, then comes back to the sign-in screen. */
export function signOut(cfg: AppConfig): void {
  forget();
  location.assign(logoutUrl(cfg));
}
